import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, ImageIcon, Save } from "lucide-react";
import { SectionHeader, Panel } from "@/components/admin/common";

function slug(str) {
  return (str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || `item-${Date.now()}`;
}

export default function AdminMenu({
  value,
  onChange,
  onSave,
  saving,
  fullMenuImage,
  onFullMenuImageSave,
}) {
  const items = value || [];

  const addItem = () => {
    const next = [
      ...items,
      {
        id: `new-${Date.now()}`,
        name: "New dish",
        th: "",
        desc: "",
        price: "£0.00",
        tag: "",
        image: "",
      },
    ];
    onChange(next);
  };

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (idx) => {
    if (!window.confirm("Remove this dish?")) return;
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div>
      <SectionHeader
        title="Menu dishes"
        description="Add, edit or remove dishes shown in the Menu section of the public site. Click Save after making changes."
        onSave={() => onSave(items.map((it) => ({ ...it, id: it.id || slug(it.name) })))}
        saving={saving}
      />

      <FullMenuImagePanel
        image={fullMenuImage}
        onSave={onFullMenuImageSave}
        saving={saving}
      />

      <div className="space-y-4 mt-10">
        {items.map((dish, idx) => (
          <MenuItemCard
            key={idx}
            dish={dish}
            onChange={(patch) => updateItem(idx, patch)}
            onRemove={() => removeItem(idx)}
          />
        ))}
      </div>

      <div className="mt-6">
        <Button
          type="button"
          onClick={addItem}
          data-testid="admin-add-dish"
          variant="outline"
          className="rounded-full h-11 px-5 border-dashed"
        >
          <Plus className="h-4 w-4 mr-2" /> Add a dish
        </Button>
      </div>
    </div>
  );
}

function FullMenuImagePanel({ image, onSave, saving }) {
  const [uploading, setUploading] = useState(false);
  const [draftUrl, setDraftUrl] = useState(image || "");

  React.useEffect(() => {
    setDraftUrl(image || "");
  }, [image]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      if (compressed.size > 6 * 1024 * 1024) {
        toast.error("Image is still too large after compression (max 6MB).");
        return;
      }
      const form = new FormData();
      form.append("file", compressed);
      const { data } = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraftUrl(data.data_url);
      toast.success("Image uploaded — click Save to publish.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const dirty = draftUrl !== (image || "");

  return (
    <Panel>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
        <div>
          <h3 className="font-display font-bold text-lg">Full printed menu photo</h3>
          <p className="mt-1 text-sm text-[#5C534D] max-w-xl">
            This is the large clickable menu image shown beneath the dish grid.
            Upload a new photo of your printed menu any time prices or dishes
            change.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => onSave(draftUrl)}
          disabled={!dirty || saving}
          data-testid="admin-full-menu-save"
          className="rounded-full bg-[#E53935] hover:bg-[#C62828] text-white h-10 px-5 text-sm"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save photo</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
        <div className="md:col-span-7">
          <div className="rounded-2xl overflow-hidden border border-[#E8DED5] bg-[#F5ECE1]">
            {draftUrl ? (
              <img
                src={draftUrl}
                alt="Full menu preview"
                className="w-full h-auto object-contain"
              />
            ) : (
              <div className="aspect-[4/3] grid place-items-center text-[#5C534D]">
                <ImageIcon className="h-10 w-10" />
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-5 space-y-4">
          <label className="block">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={onFile}
              className="hidden"
              data-testid="admin-full-menu-file"
            />
            <span className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-[#F5ECE1] hover:bg-[#E8DED5] cursor-pointer text-sm font-semibold">
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Upload new menu photo
                </>
              )}
            </span>
          </label>

          <div>
            <Label>Or paste an image URL</Label>
            <Input
              data-testid="admin-full-menu-url"
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              placeholder="https://..."
              className="h-11 rounded-xl mt-1 font-mono text-xs"
            />
          </div>

          {draftUrl && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDraftUrl("")}
              className="text-[#E53935] hover:bg-[#E53935]/10 h-9 rounded-full"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Remove photo
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
}

function MenuItemCard({ dish, onChange, onRemove }) {
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      if (compressed.size > 6 * 1024 * 1024) {
        toast.error("Image is still too large after compression (max 6MB).");
        return;
      }
      const form = new FormData();
      form.append("file", compressed);
      const { data } = await api.post("/admin/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ image: data.data_url });
      toast.success("Image attached — remember to save.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      data-testid={`admin-dish-${dish.id}`}
      className="rounded-3xl bg-white border border-[#E8DED5] p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-5"
    >
      <div className="md:col-span-3">
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#F5ECE1] border border-[#E8DED5]">
          {dish.image ? (
            <img
              src={dish.image}
              alt={dish.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full grid place-items-center text-[#5C534D]">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
        </div>
        <label className="mt-3 block">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFile}
            className="hidden"
            data-testid={`admin-dish-image-${dish.id}`}
          />
          <span className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-full bg-[#F5ECE1] hover:bg-[#E8DED5] cursor-pointer text-sm font-semibold">
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Upload className="h-4 w-4" /> {dish.image ? "Replace image" : "Upload image"}
              </>
            )}
          </span>
        </label>
        {dish.image && (
          <button
            type="button"
            onClick={() => onChange({ image: "" })}
            className="mt-2 text-xs text-[#5C534D] hover:text-[#E53935]"
          >
            Remove image
          </button>
        )}
      </div>

      <div className="md:col-span-9 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input
              value={dish.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="h-11 rounded-xl mt-1"
            />
          </div>
          <div>
            <Label>Thai name</Label>
            <Input
              value={dish.th}
              onChange={(e) => onChange({ th: e.target.value })}
              placeholder="ชื่อไทย"
              className="h-11 rounded-xl mt-1"
            />
          </div>
          <div>
            <Label>Price</Label>
            <Input
              value={dish.price}
              onChange={(e) => onChange({ price: e.target.value })}
              placeholder="£12.99"
              className="h-11 rounded-xl mt-1"
            />
          </div>
          <div>
            <Label>Tag (badge)</Label>
            <Input
              value={dish.tag}
              onChange={(e) => onChange({ tag: e.target.value })}
              placeholder="Signature · Spicy · Chef's Pick"
              className="h-11 rounded-xl mt-1"
            />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={dish.desc}
            onChange={(e) => onChange({ desc: e.target.value })}
            rows={3}
            className="rounded-xl mt-1"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onRemove}
            className="text-[#E53935] hover:bg-[#E53935]/10"
            data-testid={`admin-delete-dish-${dish.id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete dish
          </Button>
        </div>
      </div>
    </div>
  );
}
